import { Link, useParams } from "react-router-dom";
import { Head } from "@/components/Head";
import { Button } from "@/components/Button";
import { CtaBand } from "@/components/CtaBand";
import { Eyebrow, Section, SectionHeading } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { verticals, type VerticalSlug } from "@/data/verticals";

/**
 * VerticalPage is a single reusable template driven entirely by the `:trade`
 * route param. The four trades already modeled in `@/data/verticals` supply
 * their core copy from that shared dataset; a fifth trade — appliance repair —
 * isn't in that dataset yet, so it's fully authored in the local content map
 * below. Every trade also gets template-specific enrichment (feature
 * highlights, a hero image, a testimonial) that the shared dataset doesn't
 * carry, defined locally per slug.
 */

type TradeSlug = VerticalSlug | "appliance";

const TRADE_ORDER: TradeSlug[] = [
  "hvac",
  "plumbing",
  "electrical",
  "roofing",
  "appliance",
];

function isTradeSlug(value: string): value is TradeSlug {
  return (TRADE_ORDER as string[]).includes(value);
}

type TradeBase = {
  slug: TradeSlug;
  name: string;
  headline: string;
  description: string;
  painPoints: string[];
  outcomes: string[];
  modules: string[];
};

// The appliance-repair vertical isn't in the shared dataset yet, so it's
// authored here in full. Shape matches TradeBase exactly so it can sit
// alongside the four verticals pulled from @/data/verticals below.
const applianceBase: TradeBase = {
  slug: "appliance",
  name: "Appliance Repair",
  headline: "Run appliance repair calls from first ring to paid invoice",
  description:
    "Refrigerators, washers, dryers, ranges, and OEM warranty work all move fast or they don't move at all. ServiTrex keeps intake, parts, and invoicing in sync so a same-day repair call doesn't turn into a three-visit ordeal.",
  painPoints: [
    "Techs guessing part numbers on-site instead of quoting a fixed price",
    "Warranty and out-of-warranty jobs billed the same way by mistake",
    "Repeat visits because the wrong part shipped to the wrong truck",
    "No record of which models keep failing for which customers",
  ],
  outcomes: [
    "Model and serial lookups pull parts and pricing before the tech knocks",
    "Warranty rules apply automatically so billing matches the coverage",
    "Truck stock and PO status visible before a second trip gets booked",
    "Appliance and repair history follow the property across every call",
  ],
  modules: [
    "AI voice intake",
    "Pricebook & flat-rate quoting",
    "Inventory & parts tracking",
    "TrexField diagnostics",
    "Customer portal",
    "Invoicing",
  ],
};

const tradeBases: Record<TradeSlug, TradeBase> = {
  hvac: verticals.hvac,
  plumbing: verticals.plumbing,
  electrical: verticals.electrical,
  roofing: verticals.roofing,
  appliance: applianceBase,
};

type FeatureHighlight = { title: string; body: string };
type Testimonial = { quote: string; name: string; role: string };
type HeroImage = {
  src: string;
  alt: string;
  width: number;
  height: number;
  aspectClass: string;
};

type TradeContent = {
  heroImage: HeroImage;
  featureHighlights: [FeatureHighlight, FeatureHighlight, FeatureHighlight];
  testimonial: Testimonial;
  crossProductNote: string;
};

const tradeContent: Record<TradeSlug, TradeContent> = {
  hvac: {
    heroImage: {
      src: "/img/mktg-hero-dashboard.jpg",
      alt: "Dispatch dashboard showing HVAC technician assignments across a service day",
      width: 1376,
      height: 768,
      aspectClass: "aspect-[16/9]",
    },
    featureHighlights: [
      {
        title: "Manual J-informed estimates",
        body: "Load calculations and equipment sizing feed straight into the estimate, so a replacement quote reflects the actual house — not a rule of thumb from the truck.",
      },
      {
        title: "Maintenance agreements that renew themselves",
        body: "Agreements track against real service history and equipment age, so renewal calls go out ahead of a breakdown instead of after one.",
      },
      {
        title: "Equipment history that travels with the property",
        body: "Install date, model, refrigerant type, and every service log are on the job the moment a tech opens it in TrexField.",
      },
    ],
    testimonial: {
      quote:
        "We stopped losing after-hours no-cool calls to whoever answered first. AI voice books the diagnostic, and the tech shows up already knowing the unit.",
      name: "Dana Whitfield",
      role: "Service Manager, multi-location HVAC group",
    },
    crossProductNote:
      "Seasonal surge doesn't wait for office hours. AI voice covers the 2am no-cool call, dispatch balances it against tomorrow's maintenance route, and TrexField hands the tech full equipment history before the truck's in park.",
  },
  plumbing: {
    heroImage: {
      src: "/img/mktg-field-tech.jpg",
      alt: "Plumbing technician reviewing a job on a phone at a customer's property",
      width: 1200,
      height: 896,
      aspectClass: "aspect-[4/3]",
    },
    featureHighlights: [
      {
        title: "Emergency-aware dispatch",
        body: "Leaks, no-water calls, and burst pipes triage ahead of scheduled installs and route to the closest qualified truck automatically.",
      },
      {
        title: "Flat-rate pricebook, not truck-side math",
        body: "Every tech quotes from the same flat-rate book, so a water heater replacement prices the same in Truck 3 as it does in Truck 7.",
      },
      {
        title: "Photo and warranty documentation that sticks",
        body: "Before-and-after photos and manufacturer warranty terms attach to the invoice automatically — not lost in a text thread three weeks later.",
      },
    ],
    testimonial: {
      quote:
        "Our CSRs finally quote the same price the tech does. The pricebook ended the 'let me check with dispatch' calls for good.",
      name: "Rosa Delgado",
      role: "Operations Lead, residential & commercial plumbing",
    },
    crossProductNote:
      "AI voice separates the burst pipe from the routine drain cleaning before either hits the board. Dispatch protects capacity for both. TrexField closes the job with photos and pricebook line items that match the invoice exactly.",
  },
  electrical: {
    heroImage: {
      src: "/img/mktg-hero-dashboard.jpg",
      alt: "Dispatch dashboard showing skill-tagged electrical technicians across active jobs",
      width: 1376,
      height: 768,
      aspectClass: "aspect-[16/9]",
    },
    featureHighlights: [
      {
        title: "Permit and inspection checkpoints built into the job",
        body: "Job stages include permitting and inspection steps directly, so a panel upgrade or EV charger install never quietly stalls waiting on paperwork.",
      },
      {
        title: "Skill-tagged dispatch for licensed work",
        body: "Panel, generator, and EV charger jobs route only to techs with the right license and certification logged against their profile.",
      },
      {
        title: "Change orders the office sees the moment they happen",
        body: "Field notes and photos update the job record live, so a change order doesn't wait for a callback at five o'clock.",
      },
    ],
    testimonial: {
      quote:
        "Tracking inspections used to live in someone's notebook. Now a job can't slip through final sign-off without the system flagging it.",
      name: "Tomas Reyes",
      role: "Field Operations Manager, commercial & residential electrical",
    },
    crossProductNote:
      "AI voice captures the scope on the first call — service, panel, or project work. Dispatch matches licensed skill sets to the job. TrexField keeps permits, inspections, and change orders visible to the office in real time.",
  },
  roofing: {
    heroImage: {
      src: "/img/mktg-ai-voice.jpg",
      alt: "Visual representation of an AI voice agent capturing a storm-related roofing lead",
      width: 1200,
      height: 896,
      aspectClass: "aspect-[4/3]",
    },
    featureHighlights: [
      {
        title: "Aerial measurements attached to the estimate",
        body: "Roof measurement reports and inspection photos live on the opportunity, so an estimate reflects actual squares — not a curb guess.",
      },
      {
        title: "Financing and insurance workflows built in",
        body: "Present financing options and manage insurance claim documentation without leaving the job record.",
      },
      {
        title: "Estimate turnaround fast enough to beat the next knock",
        body: "Shared pricebooks plus attached measurements produce a proposal same-day, not after a storm-week backlog.",
      },
    ],
    testimonial: {
      quote:
        "After a hailstorm, the phones don't stop. AI voice and CSRs captured every lead this season — we didn't lose a single call to voicemail.",
      name: "Carla Jennings",
      role: "General Manager, storm & residential roofing",
    },
    crossProductNote:
      "AI voice and CSRs absorb the storm-week spike without dropping a lead. Dispatch schedules inspections against production capacity. TrexField carries measurements, photos, and sales notes straight to the crew.",
  },
  appliance: {
    heroImage: {
      src: "/img/mktg-field-tech.jpg",
      alt: "Appliance repair technician reviewing a work order on a phone in a customer's kitchen",
      width: 1200,
      height: 896,
      aspectClass: "aspect-[4/3]",
    },
    featureHighlights: [
      {
        title: "Model-and-serial-aware quoting",
        body: "Enter the model and serial number and the right part numbers and flat-rate pricing follow — no more guessing at the truck.",
      },
      {
        title: "Warranty rules built into billing",
        body: "In-warranty, extended-warranty, and out-of-warranty jobs each bill correctly the first time, with paperwork to back it up.",
      },
      {
        title: "Repair-or-replace math on the doorstep",
        body: "Unit age, repair cost, and replacement value surface together, so the call gets made — and trusted — in the living room, not after a second visit.",
      },
    ],
    testimonial: {
      quote:
        "Our second-trip rate dropped the month we started pulling parts against the model number instead of a tech's memory.",
      name: "Marcus Ibe",
      role: "Owner, regional appliance repair company",
    },
    crossProductNote:
      "AI voice captures brand, model, and symptom before a CSR ever picks up. Dispatch routes by brand certification. TrexField hands the tech a parts list and warranty status before the doorbell rings.",
  },
};

export function VerticalPage() {
  const { trade = "" } = useParams();
  const slug = trade.toLowerCase();

  if (!isTradeSlug(slug)) {
    return <VerticalNotFound requested={trade} />;
  }

  const base = tradeBases[slug];
  const content = tradeContent[slug];
  const tradeLower = base.name.toLowerCase();

  return (
    <>
      <Head
        title={`${base.name} software — ServiTrex for ${tradeLower} teams`}
        description={base.description}
      />

      <Section className="pt-14 sm:pt-16">
        <nav aria-label="Choose a trade" className="flex flex-wrap gap-2">
          {TRADE_ORDER.map((item) => {
            const isCurrent = item === slug;
            return (
              <Link
                key={item}
                to={`/verticals/${item}`}
                aria-current={isCurrent ? "page" : undefined}
                className={
                  isCurrent
                    ? "rounded-full bg-ink px-4 py-2 text-sm font-semibold text-fg-inverse"
                    : "rounded-full border border-border bg-surface-raised px-4 py-2 text-sm font-semibold text-muted hover:text-fg"
                }
              >
                {tradeBases[item].name}
              </Link>
            );
          })}
        </nav>

        <div className="mt-10 grid gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
          <div>
            <Eyebrow>{base.name}</Eyebrow>
            <h1 className="max-w-3xl text-balance text-4xl sm:text-5xl">
              {base.headline}
            </h1>
            <p className="mt-5 max-w-2xl text-pretty text-lg text-muted">
              {base.description}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button to="/demo" size="lg">
                Book a {tradeLower} demo
              </Button>
              <Button to="/pricing" variant="secondary" size="lg">
                View pricing
              </Button>
            </div>
          </div>

          <Reveal>
            <div
              className={`overflow-hidden rounded-[1.75rem] border border-border shadow-soft ${content.heroImage.aspectClass}`}
            >
              <img
                src={content.heroImage.src}
                alt={content.heroImage.alt}
                width={content.heroImage.width}
                height={content.heroImage.height}
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="bg-surface-muted/50">
        <div className="grid gap-6 lg:grid-cols-2">
          <Reveal>
            <article className="h-full rounded-[1.75rem] border border-border bg-surface-raised p-6 sm:p-8">
              <h2 className="font-display text-2xl tracking-tight sm:text-3xl">
                Where {tradeLower} teams get stuck
              </h2>
              <ul className="mt-5 space-y-3 text-sm leading-relaxed text-muted sm:text-base">
                {base.painPoints.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-1 text-accent-warm">●</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>
          <Reveal delay={0.06}>
            <article className="h-full rounded-[1.75rem] border border-border bg-ink p-6 text-fg-inverse sm:p-8">
              <h2 className="font-display text-2xl tracking-tight sm:text-3xl">
                What changes with ServiTrex
              </h2>
              <ul className="mt-5 space-y-3 text-sm leading-relaxed text-fg-inverse/75 sm:text-base">
                {base.outcomes.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-1 text-teal">●</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Built for the trade"
          title={`What ${tradeLower} teams get that generic FSM software doesn't`}
          description={`Three things ${tradeLower} operators ask for in the first demo call — built in, not bolted on.`}
        />
        <div className="mt-10 grid gap-5 lg:grid-cols-3 lg:grid-rows-2">
          <Reveal className="lg:col-span-2 lg:row-span-2">
            <article className="flex h-full flex-col justify-end rounded-[1.75rem] border border-border bg-surface-raised p-7 shadow-soft sm:p-9">
              <div className="mb-6 h-1.5 w-14 rounded-full bg-teal" />
              <h3 className="font-display text-2xl tracking-tight sm:text-3xl">
                {content.featureHighlights[0].title}
              </h3>
              <p className="mt-3 max-w-xl text-pretty text-muted sm:text-lg">
                {content.featureHighlights[0].body}
              </p>
            </article>
          </Reveal>
          <Reveal delay={0.06}>
            <article className="h-full rounded-[1.75rem] border border-border bg-surface-raised p-6">
              <div className="mb-4 h-1.5 w-10 rounded-full bg-teal" />
              <h3 className="font-display text-xl tracking-tight">
                {content.featureHighlights[1].title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted">
                {content.featureHighlights[1].body}
              </p>
            </article>
          </Reveal>
          <Reveal delay={0.12}>
            <article className="h-full rounded-[1.75rem] border border-border bg-ink p-6 text-fg-inverse">
              <div className="mb-4 h-1.5 w-10 rounded-full bg-teal" />
              <h3 className="font-display text-xl tracking-tight">
                {content.featureHighlights[2].title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-fg-inverse/75">
                {content.featureHighlights[2].body}
              </p>
            </article>
          </Reveal>
        </div>
      </Section>

      <Section className="bg-surface-muted/50">
        <SectionHeading
          eyebrow="Modules"
          title={`Modules that matter for ${tradeLower}`}
          description="Turn these on as the business grows. Every module shares the same customer, property, and job record."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {base.modules.map((module, index) => (
            <Reveal key={module} delay={(index % 3) * 0.05}>
              <div className="flex h-full items-center gap-3 rounded-2xl border border-border bg-surface-raised px-5 py-4">
                <span
                  aria-hidden
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-teal/15 text-sm font-semibold text-teal-deep"
                >
                  ✓
                </span>
                <span className="text-sm font-medium text-fg sm:text-base">
                  {module}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <Reveal>
            <figure className="rounded-[2rem] border border-border bg-ink p-8 text-fg-inverse shadow-soft sm:p-10">
              <blockquote className="font-display text-2xl tracking-tight text-balance sm:text-3xl">
                “{content.testimonial.quote}”
              </blockquote>
              <figcaption className="mt-6 text-sm text-fg-inverse/70">
                <span className="font-semibold text-fg-inverse">
                  {content.testimonial.name}
                </span>
                {" — "}
                {content.testimonial.role}
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.06}>
            <div>
              <h2 className="max-w-xl font-display text-3xl tracking-tight">
                Office, TrexField, and AI voice — tuned for {tradeLower} work
              </h2>
              <p className="mt-4 max-w-xl text-pretty text-muted">
                {content.crossProductNote}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button to="/product/office" variant="secondary">
                  ServiTrex office
                </Button>
                <Button to="/product/field" variant="secondary">
                  TrexField
                </Button>
                <Button to="/signup">Pick a plan</Button>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      <CtaBand
        title={`Show us how your ${tradeLower} day actually runs`}
        body="Bring a real call pattern, dispatch constraint, or membership motion. We'll map it onto ServiTrex without generic slides."
      />
    </>
  );
}

function VerticalNotFound({ requested }: { requested: string }) {
  const label = requested.trim() || "that trade";

  return (
    <>
      <Head
        title="Vertical not found — ServiTrex"
        description="ServiTrex ships trade-tuned pages for HVAC, plumbing, electrical, roofing, and appliance repair. Find the right one here."
      />
      <Section className="pt-20 sm:pt-24">
        <div className="max-w-2xl">
          <Eyebrow>Verticals</Eyebrow>
          <h1 className="text-balance text-4xl sm:text-5xl">
            We don't have a page for "{label}" yet.
          </h1>
          <p className="mt-5 text-pretty text-lg text-muted">
            ServiTrex ships trade-tuned workflows for the home-service verticals
            below. Pick one to see the pains, modules, and outcomes built for
            that trade — or talk to us about yours.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {TRADE_ORDER.map((slug) => (
              <Button key={slug} to={`/verticals/${slug}`} variant="secondary">
                {tradeBases[slug].name}
              </Button>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button to="/demo">Book a demo</Button>
            <Button to="/" variant="ghost">
              Back home
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}
